import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Plus, Search, Edit, Trash2, Eye, Package,
  Phone, Mail, Globe, RefreshCw, MoreVertical
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const vendorTypes = [
  { value: 'publication', label: 'Publication (Online)' },
  { value: 'magazine', label: 'Magazine (Print)' },
  { value: 'newspaper', label: 'Newspaper (Print)' },
  { value: 'social_page', label: 'Social Page' },
  { value: 'agency', label: 'PR Agency' },
  { value: 'other', label: 'Other' }
];

export default function AdminVendors() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    vendor_type: 'publication',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    payment_terms: '',
    gst_number: '',
    notes: ''
  });

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/vendors`;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter) params.append('vendor_type', typeFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const openModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name || '',
        vendor_type: vendor.vendor_type || 'publication',
        contact_name: vendor.contact_name || '',
        contact_email: vendor.contact_email || '',
        contact_phone: vendor.contact_phone || '',
        website: vendor.website || '',
        payment_terms: vendor.payment_terms || '',
        gst_number: vendor.gst_number || '',
        notes: vendor.notes || ''
      });
    } else {
      setEditingVendor(null);
      setFormData({
        name: '',
        vendor_type: 'publication',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        payment_terms: '',
        gst_number: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Vendor name is required", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingVendor 
        ? `${API_URL}/api/admin/vendors/${editingVendor.id}`
        : `${API_URL}/api/admin/vendors`;

      const response = await fetch(url, {
        method: editingVendor ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: "Success", description: editingVendor ? "Vendor updated" : "Vendor created" });
        setShowModal(false);
        fetchVendors();
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail || "Failed to save", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save vendor", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/vendors/${vendorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast({ title: "Success", description: data.message });
        fetchVendors();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete vendor", variant: "destructive" });
    }
  };

  const getTypeLabel = (type) => {
    const found = vendorTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      suspended: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${styles[status] || styles.active}`}>
        {status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0d0d0d]">Vendors</h1>
            <p className="text-[#737373] mt-1">Manage your suppliers and partners</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/inventory')}>
              Back to Inventory
            </Button>
            <Button onClick={() => openModal()} className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <Input
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Types</option>
            {vendorTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Vendors Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-[#D4AF37]" />
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-[#e5e5e5]">
            <Building2 className="w-12 h-12 text-[#d4d4d4] mx-auto mb-4" />
            <p className="text-[#737373]">No vendors found</p>
            <Button onClick={() => openModal()} className="mt-4 bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]">
              Add Your First Vendor
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map((vendor) => (
              <div key={vendor.id} className="bg-white rounded-lg border border-[#e5e5e5] p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0d0d0d]">{vendor.name}</h3>
                      <p className="text-xs text-[#737373]">{getTypeLabel(vendor.vendor_type)}</p>
                    </div>
                  </div>
                  {getStatusBadge(vendor.status)}
                </div>

                <div className="space-y-2 text-sm">
                  {vendor.contact_name && (
                    <p className="text-[#525252]">{vendor.contact_name}</p>
                  )}
                  {vendor.contact_email && (
                    <div className="flex items-center gap-2 text-[#737373]">
                      <Mail className="w-3 h-3" />
                      <span className="truncate">{vendor.contact_email}</span>
                    </div>
                  )}
                  {vendor.contact_phone && (
                    <div className="flex items-center gap-2 text-[#737373]">
                      <Phone className="w-3 h-3" />
                      <span>{vendor.contact_phone}</span>
                    </div>
                  )}
                  {vendor.website && (
                    <div className="flex items-center gap-2 text-[#737373]">
                      <Globe className="w-3 h-3" />
                      <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline truncate">
                        {vendor.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-[#e5e5e5] flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-[#737373]">
                    <Package className="w-4 h-4" />
                    <span>{vendor.package_count || 0} packages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openModal(vendor)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(vendor.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-[#e5e5e5]">
                <h2 className="text-lg font-semibold text-[#0d0d0d]">
                  {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                </h2>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Vendor Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Times of India"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Type</label>
                  <select
                    value={formData.vendor_type}
                    onChange={(e) => setFormData({ ...formData, vendor_type: e.target.value })}
                    className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2"
                  >
                    {vendorTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Contact Name</label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Contact Phone</label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Contact Email</label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@vendor.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Website</label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Payment Terms</label>
                    <Input
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                      placeholder="e.g., Net 30, Advance"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0d0d0d] mb-1">GST Number</label>
                    <Input
                      value={formData.gst_number}
                      onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                      placeholder="GSTIN"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0d0d0d] mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm"
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              <div className="p-6 border-t border-[#e5e5e5] flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]">
                  {saving ? 'Saving...' : editingVendor ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
